import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Alert,
  Divider,
  Button,
  CircularProgress,
  Rating,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import WarningIcon from "@mui/icons-material/Warning";
import FavoriteIcon from "@mui/icons-material/Favorite";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import MoodIcon from "@mui/icons-material/Mood";
import MedicationIcon from "@mui/icons-material/Medication";
import dailyIntakeService from "../../services/dailyIntakeService";
import { useResolvedPatientId } from "../../hooks/useResolvedPatientId";
import { useParams } from "react-router-dom";

const SYMPTOM_LABELS = {
  fatigue: "Fatigue",
  shortnessOfBreath: "Shortness of Breath",
  chestPain: "Chest Pain",
  swelling: "Swelling",
  nausea: "Nausea",
  dizziness: "Dizziness",
  jointPain: "Joint Pain",
  numbnessTingling: "Numbness / Tingling",
};

const getSeverityColor = (value) => {
  if (value === null || value === undefined) return "default";
  if (value <= 3) return "success";
  if (value <= 6) return "warning";
  return "error";
};

export default function DailyIntakeDetail() {
  const { patientId, loading: resolvingPatientId, error: patientIdError } = useResolvedPatientId();
  const { entryId } = useParams();
  const navigate = useNavigate();

  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!patientId || !entryId) return;
    (async () => {
      setLoading(true);
      const result = await dailyIntakeService.getIntakeById(patientId, entryId);
      if (result.success) {
        setEntry(result.data);
      } else {
        setError(result.message || "Could not load this entry.");
      }
      setLoading(false);
    })();
  }, [patientId, entryId]);

  if (resolvingPatientId || loading) {
    return (
      <Container sx={{ mt: 8, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (patientIdError || error || !entry) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{patientIdError || error || "Entry not found."}</Alert>
        <Button startIcon={<ArrowBackIcon />} sx={{ mt: 2 }} onClick={() => navigate(-1)}>
          Back
        </Button>
      </Container>
    );
  }

  const hasAdverseEvent = entry.newSymptoms || entry.fellToday || entry.hospitalVisit || entry.emergencyRoom;

  const vitals = [
    { label: "Weight", value: entry.weight, unit: "lbs" },
    {
      label: "Blood Pressure",
      value: entry.bloodPressureSystolic && entry.bloodPressureDiastolic
        ? `${entry.bloodPressureSystolic}/${entry.bloodPressureDiastolic}`
        : null,
      unit: "mmHg",
    },
    { label: "Heart Rate", value: entry.heartRate, unit: "bpm" },
    { label: "Temperature", value: entry.temperature, unit: "°F" },
    { label: "Oxygen Saturation", value: entry.oxygenSaturation, unit: "%" },
  ].filter((v) => v.value !== null && v.value !== undefined);

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      {/* Screen-only controls, hidden on print */}
      <Box className="no-print" sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Back to History
        </Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          Print for Doctor Visit
        </Button>
      </Box>

      <Paper elevation={2} sx={{ p: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight="bold">
            {entry.date ? new Date(entry.date).toLocaleDateString("en-US", {
              weekday: "long", year: "numeric", month: "long", day: "numeric",
            }) : "Daily Check-In"}
          </Typography>
          {entry.hasConcerningSymptoms && (
            <Chip
              icon={<WarningIcon />}
              label="Concerning symptoms this day"
              color="error"
              sx={{ mt: 1 }}
            />
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Vitals */}
        {vitals.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <MonitorHeartIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">Vitals</Typography>
            </Box>
            <Grid container spacing={2}>
              {vitals.map((v) => (
                <Grid item xs={6} sm={4} key={v.label}>
                  <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="caption" color="text.secondary">{v.label}</Typography>
                    <Typography variant="h6">{v.value} <Typography component="span" variant="caption">{v.unit}</Typography></Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Symptoms */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <FavoriteIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">Symptoms (0-10 scale)</Typography>
          </Box>
          <Grid container spacing={1.5}>
            {Object.entries(SYMPTOM_LABELS).map(([key, label]) => (
              entry[key] !== null && entry[key] !== undefined && (
                <Grid item xs={6} sm={4} key={key}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}>
                    <Typography variant="body2">{label}</Typography>
                    <Chip label={entry[key]} size="small" color={getSeverityColor(entry[key])} />
                  </Box>
                </Grid>
              )
            ))}
          </Grid>
          {entry.averageSymptomSeverity !== null && entry.averageSymptomSeverity !== undefined && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Average symptom severity: {entry.averageSymptomSeverity.toFixed(1)} / 10
            </Typography>
          )}
        </Box>

        {/* Quality of Life */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <MoodIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">Quality of Life</Typography>
          </Box>
          <Grid container spacing={2}>
            {[
              { label: "Overall Well-being", value: entry.overallWellbeing },
              { label: "Mood", value: entry.moodRating },
              { label: "Sleep Quality", value: entry.sleepQuality },
              { label: "Appetite", value: entry.appetiteLevel },
            ].map((q) => q.value !== null && q.value !== undefined && (
              <Grid item xs={6} sm={3} key={q.label}>
                <Typography variant="caption" color="text.secondary">{q.label}</Typography>
                <Rating value={q.value} max={5} readOnly size="small" />
              </Grid>
            ))}
            {entry.activityLevel && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>Activity Level:</strong> {entry.activityLevel}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Medications & Activities */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <MedicationIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">Medications & Daily Activities</Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Medications taken:</strong> {entry.medicationsTaken ? "Yes" : "No"}
          </Typography>
          {!entry.medicationsTaken && entry.missedMedications && (
            <Alert severity="warning" sx={{ mb: 1 }}>Missed: {entry.missedMedications}</Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {entry.exerciseMinutes !== null && entry.exerciseMinutes !== undefined && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">Exercise</Typography>
                <Typography variant="body2">{entry.exerciseMinutes} min</Typography>
              </Grid>
            )}
            {entry.walkingDistance && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">Walking Distance</Typography>
                <Typography variant="body2">{entry.walkingDistance}</Typography>
              </Grid>
            )}
            {entry.mealsEaten !== null && entry.mealsEaten !== undefined && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">Meals Eaten</Typography>
                <Typography variant="body2">{entry.mealsEaten}</Typography>
              </Grid>
            )}
            {entry.waterIntake !== null && entry.waterIntake !== undefined && (
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary" display="block">Water</Typography>
                <Typography variant="body2">{entry.waterIntake} glasses</Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Adverse Events */}
        {hasAdverseEvent && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <WarningIcon color="warning" />
              <Typography variant="h6" fontWeight="bold" color="warning.main">
                Adverse Events & New Symptoms
              </Typography>
            </Box>
            {entry.newSymptoms && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                New/worsening symptoms{entry.newSymptomsDescription ? `: ${entry.newSymptomsDescription}` : ""}
              </Alert>
            )}
            {entry.fellToday && <Alert severity="warning" sx={{ mb: 1 }}>Fell or lost balance today</Alert>}
            {entry.hospitalVisit && <Alert severity="warning" sx={{ mb: 1 }}>Hospital/clinic visit today</Alert>}
            {entry.emergencyRoom && <Alert severity="error" sx={{ mb: 1 }}>Emergency room visit today</Alert>}
          </Box>
        )}

        {/* Notes */}
        {entry.additionalNotes && (
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>Notes</Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{entry.additionalNotes}</Typography>
          </Box>
        )}
      </Paper>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </Container>
  );
}